import React, { useEffect, useState } from "react";
import ReactSelect from "react-select";
import { toast } from "react-toastify";
import { isRight } from "src/base/Either";
import { DI, Injector } from "src/base/Injector";
import { Input } from "src/components/input";
import { MainButton } from "src/components/button/mainButton";
import { IAccountState } from "src/features/account/presenter/store";
import { IComnetRepository } from "src/features/comnet/data/repositories/comnetRepository";
import { IComnetMaterial, IMedicalRecordComnetMaterial } from "src/features/comnet/domain/model/comnetMaterial";
import store from "src/store";

interface ComnetMedicalRecordSectionProps {
  medicalRecordId: string;
}

const getRepository = (): IComnetRepository => {
  DI.get().injectComnetDependencies();
  return Injector.get().find<IComnetRepository>("ComnetRepository");
};

const ComnetMedicalRecordSection: React.FC<ComnetMedicalRecordSectionProps> = ({ medicalRecordId }) => {
  const [availableMaterials, setAvailableMaterials] = useState<IComnetMaterial[]>([]);
  const [shares, setShares] = useState<IMedicalRecordComnetMaterial[]>([]);
  const [selectedMaterialId, setSelectedMaterialId] = useState<number | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const hasComnetPlan = (store.getState().account as IAccountState).me.has_comnet_plan;

  const fetchAll = async () => {
    const repository = getRepository();

    const materialsEither = await repository.getMaterials();
    if (isRight(materialsEither)) {
      setAvailableMaterials(materialsEither.value.filter((material) => material.status === "done"));
    }

    if (medicalRecordId) {
      const sharesEither = await repository.getMedicalRecordMaterials(medicalRecordId);
      if (isRight(sharesEither)) {
        setShares(sharesEither.value);
      }
    }
  };

  useEffect(() => {
    if (hasComnetPlan) fetchAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [medicalRecordId]);

  // COMNETプラン未契約の医院には、そもそも欄自体を出さない
  if (!hasComnetPlan) return null;

  if (!medicalRecordId) {
    return <p className="text-muted">COMNET資料の共有は、症例を保存した後に設定できます。</p>;
  }

  const handleAdd = async () => {
    if (!selectedMaterialId) return;
    setSubmitting(true);
    const repository = getRepository();
    const either = await repository.addMedicalRecordMaterial(medicalRecordId, selectedMaterialId, true, "");
    if (isRight(either)) {
      setSelectedMaterialId(null);
      await fetchAll();
    } else {
      toast.error("COMNET資料の追加に失敗しました", { position: toast.POSITION.BOTTOM_LEFT });
    }
    setSubmitting(false);
  };

  const handleToggleShared = async (share: IMedicalRecordComnetMaterial) => {
    const newIsShared = !share.is_shared;
    setShares((prev) => prev.map((s) => (s.id === share.id ? { ...s, is_shared: newIsShared } : s)));

    const repository = getRepository();
    const either = await repository.updateMedicalRecordMaterial(share.id, newIsShared, share.comment ?? "");
    if (!isRight(either)) {
      toast.error("共有設定の更新に失敗しました", { position: toast.POSITION.BOTTOM_LEFT });
      await fetchAll();
    }
  };

  const handleCommentChange = (shareId: number, value: string) => {
    setShares((prev) => prev.map((s) => (s.id === shareId ? { ...s, comment: value } : s)));
  };

  const handleCommentSave = async (share: IMedicalRecordComnetMaterial) => {
    const repository = getRepository();
    const either = await repository.updateMedicalRecordMaterial(share.id, share.is_shared, share.comment ?? "");
    if (isRight(either)) {
      toast.success("メモを保存しました", { position: toast.POSITION.BOTTOM_LEFT });
    } else {
      toast.error("メモの保存に失敗しました", { position: toast.POSITION.BOTTOM_LEFT });
    }
  };

  const handleRemove = async (share: IMedicalRecordComnetMaterial) => {
    const repository = getRepository();
    const either = await repository.removeMedicalRecordMaterial(share.id);
    if (isRight(either)) {
      setShares((prev) => prev.filter((s) => s.id !== share.id));
    } else {
      toast.error("削除に失敗しました", { position: toast.POSITION.BOTTOM_LEFT });
    }
  };

  const addedClinicMaterialIds = new Set(shares.map((share) => share.comnet_clinic_material_id));
  const selectableMaterials = availableMaterials.filter(
    (material) => material.clinic_material_id !== null && !addedClinicMaterialIds.has(material.clinic_material_id)
  );

  return (
    <div>
      {shares.map((share) => (
        <div className="horizontal blockPdf" style={{ marginBottom: "10px", marginTop: "10px" }} key={share.id}>
          <div style={{ display: "inline-block", minWidth: "180px", verticalAlign: "middle" }}>
            {share.material_name}
          </div>
          <button
            type="button"
            className={`btn btn-sm ml10 ${share.is_shared ? "btn-primary" : "btn-secondary"}`}
            onClick={() => handleToggleShared(share)}
          >
            {share.is_shared ? "共有中" : "非共有"}
          </button>
          <Input
            className="fsc comment ml10"
            inputClassName="form-control"
            inputPlaceHolder="メモ"
            value={share.comment ?? ""}
            onChange={(value) => handleCommentChange(share.id, value)}
          />
          <button type="button" className="btn btn-sm btn-outline-secondary ml10" onClick={() => handleCommentSave(share)}>
            メモ保存
          </button>
          <button type="button" className="btn btn-sm btn-danger ml10" onClick={() => handleRemove(share)}>
            削除
          </button>
        </div>
      ))}

      <div className="horizontal" style={{ marginTop: "10px", alignItems: "center" }}>
        <div style={{ minWidth: "240px", display: "inline-block" }}>
          <ReactSelect
            placeholder="COMNET資料を選択"
            noOptionsMessage={() => "共有可能な資料がありません"}
            options={selectableMaterials.map((material) => ({
              value: material.clinic_material_id as number,
              label: material.name,
            }))}
            value={
              selectedMaterialId
                ? {
                    value: selectedMaterialId,
                    label: selectableMaterials.find((m) => m.clinic_material_id === selectedMaterialId)?.name ?? "",
                  }
                : null
            }
            onChange={(newValue) => setSelectedMaterialId(newValue?.value ?? null)}
          />
        </div>
        <MainButton
          className="btn btn-primary small-button custom-add-button ml10"
          onSubmit={handleAdd}
          enabled={!!selectedMaterialId && !submitting}
          submitting={submitting}
          title="追加"
        />
      </div>
    </div>
  );
};

export default ComnetMedicalRecordSection;